import { useSyncedRef } from "@react-hookz/web";
import axios from "axios";
import mapValues from "lodash/mapValues";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSdkContext } from "~/components/contexts";
import { useFirebaseRegistrationToken } from "~/components/dashboard-widget/display/notification-panel/hooks";
import { useFallbackStorageValue } from "~/lib/hooks";
import { subscribe, unsubscribe } from "./apis";
export const useTopicSubscriptions = () => {
  const {
    setSubscriptionList,
    subscriptionList
  } = useSubscriptionsList();
  const [subscriptions, setSubscriptions] = useState(
  // Disable sort keys rule to maintain order for display
  /* eslint-disable sort-keys-fix/sort-keys-fix */
  mapValues({
    breaking_news: "Breaking",
    scmp_exclusive: "SCMP Exclusive",
    hong_kong: "Hong Kong",
    china: "China"
  }, /* eslint-enable sort-keys-fix/sort-keys-fix */
  (value, key) => {
    var _subscriptionList$inc;
    return {
      isLoading: false,
      isSubscribed: (_subscriptionList$inc = subscriptionList === null || subscriptionList === void 0 ? void 0 : subscriptionList.includes(key)) !== null && _subscriptionList$inc !== void 0 ? _subscriptionList$inc : false,
      name: value
    };
  }));
  const {
    notification
  } = useSdkContext();
  const {
    axiosInstance
  } = useSubscriptionsAxios();
  const {
    registrationToken
  } = useFirebaseRegistrationToken(notification.firebaseApp);
  const handleSubscriptionChange = useCallback(async (key, newValue) => {
    setSubscriptions(subscriptions => {
      if (!subscriptions[key]) return subscriptions;
      return {
        ...subscriptions,
        [key]: {
          ...subscriptions[key],
          isLoading: true,
          isSubscribed: newValue
        }
      };
    });
    try {
      var _registrationToken$re, _registrationToken$re2;
      await (newValue ? subscribe(axiosInstance, {
        token: (_registrationToken$re = registrationToken.result) !== null && _registrationToken$re !== void 0 ? _registrationToken$re : "",
        topic: key
      }) : unsubscribe(axiosInstance, {
        token: (_registrationToken$re2 = registrationToken.result) !== null && _registrationToken$re2 !== void 0 ? _registrationToken$re2 : "",
        topic: key
      }));
      setSubscriptionList(subscriptionList => {
        var _subscriptionList$fil;
        return newValue ? [...(subscriptionList !== null && subscriptionList !== void 0 ? subscriptionList : []), key] : (_subscriptionList$fil = subscriptionList === null || subscriptionList === void 0 ? void 0 : subscriptionList.filter(value => value !== key)) !== null && _subscriptionList$fil !== void 0 ? _subscriptionList$fil : [];
      });
    } finally {
      setSubscriptions(subscriptions => {
        if (!subscriptions[key]) return subscriptions;
        return {
          ...subscriptions,
          [key]: {
            ...subscriptions[key],
            isLoading: false
          }
        };
      });
    }
  }, [axiosInstance, setSubscriptionList, registrationToken]);
  return {
    handleSubscriptionChange,
    subscriptions
  };
};

// This hook handle the subscription when the firebase registration token is changed
// It will refresh the subscription with new token and clean up the old subscription with old token
export const useRefreshSubscription = () => {
  const {
    notification
  } = useSdkContext();
  const {
    subscriptionList
  } = useSubscriptionsList();
  const {
    registrationToken
  } = useFirebaseRegistrationToken(notification.firebaseApp);
  const {
    axiosInstance
  } = useSubscriptionsAxios();
  const {
    remove: removeStoredRegistrationToken,
    set: setStoredRegistrationToken,
    value: storedRegistrationToken
  } = useFallbackStorageValue("firebaseToken", {
    parse: value => value,
    stringify: value => value
  });
  const [state, setState] = useState(storedRegistrationToken);
  const syncedStoredRegistrationToken = useSyncedRef(state);
  useEffect(() => {
    if (registrationToken.result === syncedStoredRegistrationToken.current) return;
    if (registrationToken.result) {
      setStoredRegistrationToken(registrationToken.result);
      setState(registrationToken.result);
    } else {
      setState(undefined);
      removeStoredRegistrationToken();
    }
    const refreshSubscription = async () => {
      if (!subscriptionList || subscriptionList.length === 0) return;
      try {
        await Promise.all(subscriptionList.map(async topic => {
          if (syncedStoredRegistrationToken.current) {
            await unsubscribe(axiosInstance, {
              token: syncedStoredRegistrationToken.current,
              topic
            });
          }
          if (registrationToken.result) {
            await subscribe(axiosInstance, {
              token: registrationToken.result,
              topic
            });
          }
        }));
      } catch (error) {
        console.error(new Error("Failed to refresh push notification subscription", {
          cause: error
        }));
      }
    };
    void refreshSubscription();
  }, [axiosInstance, registrationToken.result, removeStoredRegistrationToken, setStoredRegistrationToken, subscriptionList, syncedStoredRegistrationToken]);
};
export const useSubscriptionsList = () => {
  const {
    set: setSubscriptionList,
    value: subscriptionList
  } = useFallbackStorageValue("subscriptedList");
  return {
    setSubscriptionList,
    subscriptionList
  };
};
const useSubscriptionsAxios = () => {
  const {
    config
  } = useSdkContext();
  const axiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: config.notification.host
    });
    return instance;
  }, [config]);
  return {
    axiosInstance
  };
};